"use client";

import { cn } from "../../lib/utils";
import { toPersianDigits } from "../../utils/persianDigits";
import { ReactComponent as Tick } from "../../assets/icons/tick.svg";

export interface StepperItem {
  id: string;
  label: string;
}

type StepVisualState = "completed" | "active" | "upcoming";

export interface StepperProps {
  steps: StepperItem[];
  currentStep: number;
  ariaLabel: string;
  className?: string;
  rejectedStepIndexes?: number[];
  navigableStepIndexes?: number[];
  onStepClick?: (stepIndex: number) => void;
  inlineActiveLabel?: boolean;
}

function isStepClickable(
  index: number,
  currentStep: number,
  navigableStepIndexes: number[],
  hasStepClickHandler: boolean,
): boolean {
  if (!hasStepClickHandler) return false;
  if (index < currentStep) return true;
  return navigableStepIndexes.includes(index);
}

function getStepState(index: number, currentStep: number): StepVisualState {
  if (index < currentStep) return "completed";
  if (index === currentStep) return "active";
  return "upcoming";
}

function isStepRejected(index: number, rejectedStepIndexes: number[]): boolean {
  return rejectedStepIndexes.includes(index);
}

function getConnectorClass(
  index: number,
  currentStep: number,
  rejectedStepIndexes: number[],
): string {
  const nextIndex = index + 1;
  const nextRejected = isStepRejected(nextIndex, rejectedStepIndexes);

  if (index < currentStep - 1) {
    return nextRejected ? "border-errorVar" : "border-successVar-500";
  }
  if (index === currentStep - 1) {
    return nextRejected || isStepRejected(currentStep, rejectedStepIndexes)
      ? "border-errorVar"
      : "border-successVar-500";
  }
  return "border-primaryVar-100";
}

function getMobileSegmentClass(
  index: number,
  currentStep: number,
  rejectedStepIndexes: number[],
): string {
  const rejected = isStepRejected(index, rejectedStepIndexes);

  if (index < currentStep) {
    return rejected ? "bg-errorVar" : "bg-successVar-500";
  }
  if (index === currentStep) {
    return rejected ? "bg-errorVar" : "bg-primaryVar";
  }
  return rejected ? "bg-errorVar/40" : "bg-primaryVar-100";
}

export default function Stepper({
  steps,
  currentStep,
  ariaLabel,
  className,
  rejectedStepIndexes = [],
  navigableStepIndexes = [],
  onStepClick,
  inlineActiveLabel = false,
}: StepperProps) {
  const currentLabel = steps[currentStep]?.label ?? "";
  const currentStepRejected = isStepRejected(currentStep, rejectedStepIndexes);

  return (
    <div
      dir="rtl"
      className={cn("py-5", className)}
      aria-label={ariaLabel}
    >
      {/* Mobile / tablet (< 1024px) */}
      <div className="space-y-3 lg:hidden">
        <p
          className="
            text-right text-sm font-medium"
        >
          <span
            className={cn(
              currentStepRejected ? "text-errorVar" : "text-primaryVar-300",
            )}
          >
            مرحله {toPersianDigits(currentStep + 1)} از{" "}
            {toPersianDigits(steps.length)}
          </span>
          <span className="mx-1.5 text-primaryVar-500">•</span>
          <span
            className={cn(
              currentStepRejected ? "text-errorVar" : "text-primaryVar-500",
            )}
          >
            {" "}
            {currentLabel}
          </span>
        </p>

        <div className="flex gap-1.5">
          {steps.map((step, index) => {
            const isClickable = isStepClickable(
              index,
              currentStep,
              navigableStepIndexes,
              Boolean(onStepClick),
            );

            return (
              <button
                key={step.id}
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onStepClick?.(index)}
                aria-label={`${step.label}${isClickable ? " — رفتن به این مرحله" : ""}`}
                className={cn(
                  "h-2.5 min-w-0 flex-1 rounded-full transition-colors",
                  getMobileSegmentClass(
                    index,
                    currentStep,
                    rejectedStepIndexes,
                  ),
                  isClickable && "cursor-pointer hover:opacity-80",
                  !isClickable && "cursor-default",
                )}
              />
            );
          })}
        </div>
      </div>

      {/* Desktop (>= 1024px) */}
      <div className="hidden items-start justify-between lg:flex">
        {steps.map((step, index) => {
          const state = getStepState(index, currentStep);
          const rejected = isStepRejected(index, rejectedStepIndexes);
          const isErrorActive = state === "active" && rejected;
          const isErrorCompleted = state === "completed" && rejected;
          const stepNumber = index + 1;
          const isClickable = isStepClickable(
            index,
            currentStep,
            navigableStepIndexes,
            Boolean(onStepClick),
          );

          const handleStepClick = () => {
            if (!isClickable) return;
            onStepClick?.(index);
          };

          const circle = (
            <div
              className={cn(
                "flex size-6 items-center justify-center rounded-full text-sm font-semibold transition-colors",
                state === "completed" &&
                  !rejected &&
                  "bg-successVar-500 text-white shadow-[0_0_0_4px_rgba(34,197,94,0.18)]",
                isErrorCompleted &&
                  "bg-errorVar text-white shadow-[0_0_0_4px_rgba(239,68,68,0.18)]",
                state === "active" &&
                  !isErrorActive &&
                  "bg-primaryVar-400 text-white shadow-[0_0_0_4px_rgba(36,92,247,0.18)]",
                isErrorActive &&
                  "bg-errorVar text-white shadow-[0_0_0_4px_rgba(239,68,68,0.18)]",
                state === "upcoming" &&
                  !rejected &&
                  "border-2 border-primaryVar-300 bg-white text-primaryVar-400 shadow-[0_0_0_4px_rgba(36,92,247,0.18)]",
                state === "upcoming" &&
                  rejected &&
                  "border border-errorVar bg-errorVar-light text-errorVar shadow-[0_0_0_4px_rgba(239,68,68,0.12)]",
              )}
              aria-current={state === "active" ? "step" : undefined}
            >
              {state === "completed" && !rejected ? (
                <Tick className="size-2.5" aria-hidden />
              ) : (
                <span>{toPersianDigits(stepNumber)}</span>
              )}
            </div>
          );

          return (
            <div
              key={step.id}
              className={cn(
                "flex min-w-0 flex-1 last:flex-none",
                inlineActiveLabel ? "items-center" : "items-start",
              )}
            >
              <div
                className={cn(
                  "flex shrink-0",
                  inlineActiveLabel
                    ? "items-center"
                    : "flex-col items-center gap-2",
                )}
              >
                <button
                  type="button"
                  onClick={handleStepClick}
                  disabled={!isClickable}
                  aria-label={`${step.label}${isClickable ? " — رفتن به این مرحله" : ""}`}
                  className={cn(
                    "flex rounded-lg transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primaryVar focus-visible:ring-offset-2",
                    inlineActiveLabel
                      ? "items-center gap-1.5"
                      : "flex-col items-center gap-2",
                    isClickable && "cursor-pointer hover:opacity-80",
                    !isClickable && "cursor-default",
                  )}
                >
                  {circle}

                  {inlineActiveLabel ? (
                    state === "active" ? (
                      <span
                        className={cn(
                          "whitespace-nowrap text-xs font-semibold",
                          isErrorActive ? "text-errorVar" : "text-primaryVar-400",
                          inlineActiveLabel ? "mx-1" : "",
                        )}
                      >
                        {step.label}
                      </span>
                    ) : null
                  ) : (
                    <span
                      className={cn(
                        "whitespace-nowrap  text-center text-xs leading-5 sm:text-sm",
                        (isErrorActive ||
                          isErrorCompleted ||
                          (state === "upcoming" && rejected)) &&
                          "font-medium text-errorVar",
                        state === "active" &&
                          !rejected &&
                          "font-medium text-primaryVar",
                        state === "completed" && !rejected && "text-gray-600",
                        state === "upcoming" && !rejected && "text-gray-600",
                      )}
                    >
                      {step.label}
                    </span>
                  )}
                </button>
              </div>

              {index < steps.length - 1 ? (
                <div
                  className={cn(
                    "mx-1 h-0 min-w-4 flex-1 border-t-2 border-dashed sm:mx-2 sm:min-w-8",
                    !inlineActiveLabel && "mt-5",
                    getConnectorClass(index, currentStep, rejectedStepIndexes),
                  )}
                  aria-hidden
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
